import React, { useMemo } from 'react';
import { useTranslation } from "react-i18next";
import { getDetailsForReceiptPrint } from '../helpers/ReceiptHelper';
import { getImageURL } from "../helpers/ImageHelper";
import { QRCodeSVG } from 'qrcode.react';
import { numberToWords, getNepaliFiscalYear, getNepaliDateBS, formatReceiptDateTime } from '../helpers/BillFormatsHelper';


function __groupReceiptItems(items) {
  if (!items || !Array.isArray(items)) return items;
  const grouped = [];
  items.forEach(item => {
    const addonsKey = Array.isArray(item.addons_ids) 
      ? item.addons_ids.slice().sort().join(',') 
      : (Array.isArray(item.addons) ? item.addons.map(a=>a.id || a).sort().join(',') : '');
    const priceKey = item.price || 0;
    const variantKey = item.variant?.title || item.variant_title || '';
    const key = (item.title || item.item_title || '') + '_' + variantKey + '_' + addonsKey + '_' + priceKey;
    
    const existing = grouped.find(g => g._groupKey === key);
    if (existing) {
      existing.quantity = Number(existing.quantity) + Number(item.quantity || 1);
    } else {
      grouped.push({ ...item, _groupKey: key, quantity: Number(item.quantity || 1) });
    }
  });
  return grouped;
}

export default function PrintReceiptPage() {
  const { t } = useTranslation();
  const receiptDetails = getDetailsForReceiptPrint() || {};

  const {
    cartItems = [],
    deliveryType = '',
    customerType = '',
    customer = null,
    currency = 'Rs.',
    storeSettings = {},
    printSettings = {},
    itemsTotal = 0,
    taxTotal = 0,
    serviceChargeTotal = 0,
    payableTotal = 0,
    tokenNo = '',
    orderId = '',
    paymentMethod = '',
    multiPayments = [],
    paymentTypeDetails = null,
    cashTendered = 0,
    changeAmount = 0,
    storedDiscount = 0,
    discount = 0,
    roundOff = 0,
    paidAmount = undefined,
    dueAmount = 0,
    table_title = '',
    tableId = null,
    cashierName = '',
    isEstimate: rawIsEstimate = false
  } = receiptDetails;

  const isEstimate = Boolean(rawIsEstimate);

  const {
    store_name = '',
    address = '',
    phone = '',
    email = '',
    store_image: storeImage,
    pan_no,
    panNo,
    is_vat_registered,
    isVatRegistered,
  } = storeSettings;

  const finalPanNo = pan_no || panNo || '';
  const finalIsVat = Boolean(is_vat_registered || isVatRegistered);

  const {
    page_format = 80,
    header = '',
    footer = '',
    show_notes = 0,
    show_store_details = 1,
    show_customer_details = 1,
    print_token = 0,
    print_count = 1,
    bill_format: savedBillFormat
  } = printSettings;

  // Active bill format (check receiptDetails, then printSettings, then localStorage, default to retail_tax_invoice)
  const billFormat = receiptDetails.billFormat || savedBillFormat || localStorage.getItem('bill_format') || 'retail_tax_invoice';
  const is58 = Number(String(page_format).replace(/,/g, "")) <= 58;
  const isSheetPaper = billFormat === 'standard_a4_a5' || billFormat === 'luxury_folio';

  const fmt = (v) => `${currency} ${Number(String(v || 0).replace(/,/g, "")).toFixed(2)}`;
  const todayIso = new Date().toISOString().substring(0, 10);
  const nowLocale = new Date().toLocaleString();
  const fiscalYear = getNepaliFiscalYear(receiptDetails.created_at || receiptDetails.date || new Date());
  const explicitInvoiceNo = receiptDetails.invoice_id || receiptDetails.invoiceId || receiptDetails.invoiceNo || receiptDetails.invoice_no || receiptDetails.billNo || receiptDetails.bill_no;
  const billInvoiceNo = isEstimate
    ? (orderId ? `EST-${orderId}` : (tokenNo ? `EST-TKN-${tokenNo}` : `EST-${todayIso}`))
    : (explicitInvoiceNo ? String(explicitInvoiceNo) : (orderId ? String(orderId) : (tokenNo ? `TKN-${tokenNo}` : `BILL-${todayIso}`)));
  const amountWords = numberToWords(payableTotal, '');

  const buyerName = customer?.name || (customerType && customerType !== 'walkin' ? customerType : 'Cash Customer');
  const buyerPan = customer?.pan_no || 'N/A';
  const buyerPhone = customer?.phone || '';

  const receiptDate = receiptDetails.created_at || receiptDetails.date || new Date();
  const formattedDateTime = formatReceiptDateTime(receiptDate);
  const nepaliDate = getNepaliDateBS(receiptDate);
  const areaName = receiptDetails.area || receiptDetails.table_area || (deliveryType === 'takeaway' ? 'TAKEAWAY' : (deliveryType === 'delivery' ? 'DELIVERY' : 'HALL'));
  const tableNo = receiptDetails.table_number || receiptDetails.table_id || receiptDetails.tableId || (table_title ? table_title.replace(/\D/g, '') : '') || '6';
  const tableName = receiptDetails.tableName || receiptDetails.table_title || (tableNo ? `TH-${tableNo}` : 'Table');

  const addonString = (addons, addons_ids) =>
    (addons_ids || [])
      .map((id) => (addons || []).find((a) => a && (a.id === id || String(a.id) === String(id)))?.title || '')
      .filter(Boolean)
      .join(', ');

  return (
    <>
      <style>{`
        /* Global Reset */
        * { box-sizing: border-box; }
        body { margin: 0; padding: 0; background: #fff; color: #000; }

        @media print {
          body * { visibility: hidden !important; }
          .printable-receipt, .printable-receipt * { visibility: visible !important; }
          .printable-receipt {
            position: absolute;
            left: 0;
            top: 0;
            width: ${isSheetPaper ? '100%' : `${page_format}mm`} !important;
            page-break-after: always;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          @page {
            size: ${isSheetPaper ? 'A5 portrait' : `${page_format}mm auto`};
            margin: ${isSheetPaper ? '8mm' : '0'};
          }
        }
      `}</style>

      <div className="printable-receipt" role="document">
        {Array.from({ length: print_count || 1 }).map((_, copyIndex) => (
          <div key={copyIndex} style={{ pageBreakAfter: 'always' }}>
            {billFormat === 'retail_tax_invoice' && (
              <RetailTaxInvoiceBill
                store_name={store_name}
                address={address}
                phone={phone}
                finalPanNo={finalPanNo}
                finalIsVat={finalIsVat}
                billInvoiceNo={billInvoiceNo}
                fiscalYear={fiscalYear}
                nowLocale={nowLocale}
                deliveryType={deliveryType}
                table_title={table_title}
                buyerName={buyerName}
                buyerPan={buyerPan}
                buyerPhone={buyerPhone}
                cartItems={__groupReceiptItems(cartItems)}
                addonString={addonString}
                show_notes={show_notes}
                fmt={fmt}
                itemsTotal={itemsTotal}
                taxTotal={taxTotal}
                serviceChargeTotal={serviceChargeTotal}
                storedDiscount={storedDiscount || discount || 0}
                roundOff={roundOff}
                payableTotal={payableTotal}
                paidAmount={paidAmount}
                dueAmount={dueAmount}
                cashTendered={cashTendered}
                changeAmount={changeAmount}
                paymentMethod={paymentMethod}
                multiPayments={multiPayments}
                amountWords={amountWords}
                footer={footer}
                currency={currency}
                isEstimate={isEstimate}
                is58={is58}
                taxableAmount={receiptDetails.taxableAmount}
                area={areaName}
                tableNo={tableNo}
                tableName={tableName}
                transactionDate={formattedDateTime}
                nepaliDate={nepaliDate}
              />
            )}

            {billFormat === 'thermal_ird' && (
              <ThermalIRDBill
                store_name={store_name}
                address={address}
                phone={phone}
                finalPanNo={finalPanNo}
                finalIsVat={finalIsVat}
                billInvoiceNo={billInvoiceNo}
                fiscalYear={fiscalYear}
                nowLocale={nowLocale}
                deliveryType={deliveryType}
                table_title={table_title}
                buyerName={buyerName}
                buyerPan={buyerPan}
                buyerPhone={buyerPhone}
                cartItems={__groupReceiptItems(cartItems)}
                addonString={addonString}
                show_notes={show_notes}
                fmt={fmt}
                itemsTotal={itemsTotal}
                taxTotal={taxTotal}
                serviceChargeTotal={serviceChargeTotal}
                storedDiscount={storedDiscount || discount || 0}
                roundOff={roundOff}
                payableTotal={payableTotal}
                paidAmount={paidAmount}
                dueAmount={dueAmount}
                cashTendered={cashTendered}
                changeAmount={changeAmount}
                paymentMethod={paymentMethod}
                multiPayments={multiPayments}
                amountWords={amountWords}
                footer={footer}
                header={header}
                tokenNo={tokenNo}
                print_token={print_token}
                paymentTypeDetails={paymentTypeDetails}
                currency={currency}
                is58={is58}
                storeImage={storeImage}
                isEstimate={isEstimate}
              />
            )}

            {billFormat === 'modern_pos' && (
              <ModernPOSBill
                store_name={store_name}
                address={address}
                phone={phone}
                finalPanNo={finalPanNo}
                finalIsVat={finalIsVat}
                billInvoiceNo={billInvoiceNo}
                fiscalYear={fiscalYear}
                nowLocale={nowLocale}
                deliveryType={deliveryType}
                table_title={table_title}
                buyerName={buyerName}
                buyerPan={buyerPan}
                cartItems={__groupReceiptItems(cartItems)}
                addonString={addonString}
                show_notes={show_notes}
                fmt={fmt}
                itemsTotal={itemsTotal}
                taxTotal={taxTotal}
                serviceChargeTotal={serviceChargeTotal}
                storedDiscount={storedDiscount || discount || 0}
                roundOff={roundOff}
                payableTotal={payableTotal}
                paidAmount={paidAmount}
                dueAmount={dueAmount}
                cashTendered={cashTendered}
                changeAmount={changeAmount}
                paymentMethod={paymentMethod}
                multiPayments={multiPayments}
                amountWords={amountWords}
                footer={footer}
                tokenNo={tokenNo}
                print_token={print_token}
                currency={currency}
                is58={is58}
                isEstimate={isEstimate}
              />
            )}

            {billFormat === 'standard_a4_a5' && (
              <StandardA4A5Bill
                store_name={store_name}
                address={address}
                phone={phone}
                email={email}
                finalPanNo={finalPanNo}
                finalIsVat={finalIsVat}
                billInvoiceNo={billInvoiceNo}
                fiscalYear={fiscalYear}
                nowLocale={nowLocale}
                deliveryType={deliveryType}
                table_title={table_title}
                buyerName={buyerName}
                buyerPan={buyerPan}
                buyerPhone={buyerPhone}
                cartItems={__groupReceiptItems(cartItems)}
                addonString={addonString}
                show_notes={show_notes}
                fmt={fmt}
                itemsTotal={itemsTotal}
                taxTotal={taxTotal}
                serviceChargeTotal={serviceChargeTotal}
                storedDiscount={storedDiscount || discount || 0}
                roundOff={roundOff}
                payableTotal={payableTotal}
                paidAmount={paidAmount}
                dueAmount={dueAmount}
                cashTendered={cashTendered}
                changeAmount={changeAmount}
                paymentMethod={paymentMethod}
                multiPayments={multiPayments}
                amountWords={amountWords}
                footer={footer}
                header={header}
                currency={currency}
                storeImage={storeImage}
                isEstimate={isEstimate}
              />
            )}

            {billFormat === 'luxury_folio' && (
              <LuxuryFolioBill
                store_name={store_name}
                address={address}
                phone={phone}
                finalPanNo={finalPanNo}
                finalIsVat={finalIsVat}
                billInvoiceNo={billInvoiceNo}
                fiscalYear={fiscalYear}
                nowLocale={nowLocale}
                deliveryType={deliveryType}
                table_title={table_title}
                buyerName={buyerName}
                buyerPan={buyerPan}
                buyerPhone={buyerPhone}
                cartItems={__groupReceiptItems(cartItems)}
                addonString={addonString}
                show_notes={show_notes}
                fmt={fmt}
                itemsTotal={itemsTotal}
                taxTotal={taxTotal}
                serviceChargeTotal={serviceChargeTotal}
                storedDiscount={storedDiscount || discount || 0}
                roundOff={roundOff}
                payableTotal={payableTotal}
                paidAmount={paidAmount}
                dueAmount={dueAmount}
                paymentMethod={paymentMethod}
                multiPayments={multiPayments}
                amountWords={amountWords}
                footer={footer}
                currency={currency}
                isEstimate={isEstimate}
              />
            )}
          </div>
        ))}
      </div>
    </>
  );
}

// =========================================================================
// 0. NEPAL RETAIL TAX INVOICE BILL (SMTECH / POS 80MM EXACT REPLICA)
// =========================================================================
function RetailTaxInvoiceBill(props) {
  const {
    store_name, address, phone, finalPanNo, finalIsVat, billInvoiceNo,
    fiscalYear, nowLocale, deliveryType, table_title, buyerName, buyerPan,
    cartItems, addonString, show_notes, fmt, itemsTotal, taxTotal,
    serviceChargeTotal, storedDiscount, roundOff, payableTotal, paidAmount,
    dueAmount, cashTendered, changeAmount, paymentMethod, multiPayments, amountWords, footer,
    currency, isEstimate, is58, taxableAmount, area, tableNo, tableName, transactionDate, nepaliDate
  } = props;

  const displayStoreName = store_name || "demo";
  const displayAddress = address || "demo";
  const displayPhone = phone || "";
  const displaySellerPan = finalPanNo || "";
  const displayBuyerPan = (buyerPan && buyerPan !== "N/A" && buyerPan !== "Cash Customer") ? buyerPan : "";
  const displayArea = area || (deliveryType === 'takeaway' ? 'TAKEAWAY' : (deliveryType === 'delivery' ? 'DELIVERY' : 'HALL'));
  const displayTableNo = tableNo || (table_title ? table_title.replace(/\D/g, '') : '') || '';
  const displayTableName = tableName || table_title || (displayTableNo ? `Table ${displayTableNo}` : 'Table');
  const displayTxnDate = transactionDate || nowLocale;
  const displayNepaliDate = nepaliDate || getNepaliDateBS(new Date());
  const wordsOnly = (amountWords || "").replace(/Rupees/gi, "").trim();

  const subTotalNum = Number(String(itemsTotal || 0).replace(/,/g, ''));
  const discountNum = Number(String(storedDiscount || 0).replace(/,/g, ''));
  const discountPercent = subTotalNum > 0 && discountNum > 0 ? Math.round((discountNum / subTotalNum) * 100) : 0;
  const scNum = Number(String(serviceChargeTotal || 0).replace(/,/g, ''));
  
  // Taxable amount = Subtotal - Discount + Service Charge
  const taxableAmt = taxableAmount !== undefined && taxableAmount !== null 
    ? Number(String(taxableAmount).replace(/,/g, ''))
    : Math.max(0, subTotalNum - discountNum + scNum);
    
  const taxNum = Number(String(taxTotal || 0).replace(/,/g, ''));
  const roundOffNum = Number(String(roundOff || 0).replace(/,/g, ''));
  const grandTotalNum = Number(String(payableTotal || 0).replace(/,/g, ''));

  return (
    <div style={{
      width: is58 ? '48mm' : '72mm',
      maxWidth: is58 ? '48mm' : '72mm',
      margin: '0 auto',
      padding: is58 ? '2mm 1mm' : '3mm 2mm',
      boxSizing: 'border-box',
      fontFamily: "'Consolas', 'Courier New', Courier, Monaco, monospace",
      fontSize: is58 ? '10.5px' : '12px', fontWeight: 600,
      lineHeight: is58 ? '1.25' : '1.35',
      color: '#000',
      background: '#fff',
      WebkitFontSmoothing: 'antialiased',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: is58 ? '14px' : '17px', fontWeight: 900, lineHeight: 1.15 }}>
          {displayStoreName}
        </div>
        <div style={{ fontSize: is58 ? '10px' : '12px', fontWeight: 500, marginTop: '1px' }}>
          {displayAddress}
        </div>
        {displayPhone && (
          <div style={{ fontSize: is58 ? '10px' : '12px', fontWeight: 500 }}>
            {displayPhone}
          </div>
        )}
        <div style={{ fontSize: is58 ? '11px' : '13px', fontWeight: 700, marginTop: '3px', textTransform: 'uppercase' }}>
          {isEstimate ? 'Estimate Bill' : 'Tax Invoice'}
        </div>
      </div>

      {/* Metadata */}
      <div style={{ marginTop: '6px', fontSize: is58 ? '9px' : '10.5px', lineHeight: 1.3 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>{displaySellerPan ? `PAN: ${displaySellerPan}` : ''}</span>
          <span>{displayBuyerPan ? `Buyer PAN: ${displayBuyerPan}` : ''}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 600 }}>Bill No:</span>
          <span>{billInvoiceNo}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 600 }}>Area:</span>
          <span>{displayArea}</span>
        </div>
        {displayTableNo && (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 600 }}>Table No:</span>
            <span>{displayTableNo}</span>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 600 }}>Date:</span>
          <span>{displayTxnDate}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 600 }}>Miti:</span>
          <span>{displayNepaliDate}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 600 }}>Fiscal Year:</span>
          <span>{fiscalYear}</span>
        </div>
      </div>

      <div style={{ borderTop: '1px solid #000', margin: '4px 0' }}></div>

      {/* Items Table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: is58 ? '9px' : '10.5px', tableLayout: 'fixed' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #000' }}>
            {is58 ? (
              <>
                <th style={{ textAlign: 'left', padding: '2px 0', width: '46%' }}>Item</th>
                <th style={{ textAlign: 'right', padding: '2px 0', width: '16%' }}>QTY</th>
                <th style={{ textAlign: 'right', padding: '2px 0', width: '18%' }}>Price</th>
                <th style={{ textAlign: 'right', padding: '2px 0', width: '20%' }}>Total</th>
              </>
            ) : (
              <>
                <th style={{ textAlign: 'left', padding: '2px 0', width: '40%' }}>Item</th>
                <th style={{ textAlign: 'center', padding: '2px 0', width: '12%' }}>HSCode</th>
                <th style={{ textAlign: 'right', padding: '2px 0', width: '14%' }}>QTY</th>
                <th style={{ textAlign: 'right', padding: '2px 0', width: '16%' }}>Price</th>
                <th style={{ textAlign: 'right', padding: '2px 0', width: '18%' }}>Total</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {cartItems.map((it, idx) => {
            const qtyNum = Number(it.quantity || 1);
            const safeNum = (val) => Number(String(val || 0).replace(/,/g, '')) || 0;
            const pBase = safeNum(it.price);
            const priceNum = pBase;
            const lineTotalNum = qtyNum * priceNum;
            const hscodeVal = it.hscode || it.hs_code || '0';
            const itemNotes = it.notes;
            const itemAddons = addonString(it.addons, it.addons_ids);

            return (
              <tr key={idx}>
                <td style={{ textAlign: 'left', padding: '1.5px 0', wordBreak: 'break-word' }}>
                  {it.title}{it.variant?.title ? ` (${it.variant.title})` : (it.variant_title ? ` (${it.variant_title})` : '')}
                  {itemAddons && <div style={{ fontSize: '8px', color: '#444' }}>+ {itemAddons}</div>}
                  {show_notes && itemNotes && <div style={{ fontSize: '8px', fontStyle: 'italic' }}>* {itemNotes}</div>}
                </td>
                {!is58 && <td style={{ textAlign: 'center', padding: '1.5px 0' }}>{hscodeVal}</td>}
                <td style={{ textAlign: 'right', padding: '1.5px 0', whiteSpace: 'nowrap' }}>{Number(qtyNum).toFixed(qtyNum % 1 === 0 ? 0 : 2)}</td>
                <td style={{ textAlign: 'right', padding: '1.5px 0', whiteSpace: 'nowrap' }}>{priceNum.toFixed(2)}</td>
                <td style={{ textAlign: 'right', padding: '1.5px 0', whiteSpace: 'nowrap' }}>{lineTotalNum.toFixed(2)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div style={{ borderTop: '1px solid #000', margin: '4px 0' }}></div>

      {/* Payment Box & Totals */}
      {is58 ? (
        /* 58mm Stacked Layout to Prevent Clipping */
        <div style={{ margin: '3px 0' }}>
          <div style={{ fontSize: '9.5px', lineHeight: '1.35', marginBottom: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingRight: '1px' }}>
              <span>Sub-Total :</span>
              <span>{subTotalNum.toFixed(2)}</span>
            </div>
            {discountNum > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingRight: '1px', color: '#000', fontWeight: 700 }}>
                <span>Discount {discountPercent > 0 ? `(${discountPercent}%)` : ''} :</span>
                <span>-{discountNum.toFixed(2)}</span>
              </div>
            )}
            {scNum > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingRight: '1px' }}>
                <span>Service Charge :</span>
                <span>+{scNum.toFixed(2)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingRight: '1px', fontWeight: 600 }}>
              <span>{finalIsVat ? 'Taxable AMT :' : 'NonTaxable AMT :'}</span>
              <span>{taxableAmt.toFixed(2)}</span>
            </div>
            {finalIsVat && taxNum > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingRight: '1px' }}>
                <span>13% VAT :</span>
                <span>+{taxNum.toFixed(2)}</span>
              </div>
            )}
            {roundOffNum !== 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingRight: '1px' }}>
                <span>Round Off :</span>
                <span>{roundOffNum > 0 ? `+${roundOffNum.toFixed(2)}` : roundOffNum.toFixed(2)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingRight: '1px', fontWeight: 700 }}>
              <span>Total :</span>
              <span>{grandTotalNum.toFixed(2)}</span>
            </div>
          </div>
          <div style={{ border: '1px solid #000', padding: '2.5px 4px', fontSize: '9px' }}>
            <span style={{ fontWeight: 700 }}>Mode: </span>
            {multiPayments && multiPayments.length > 0 ? (
              <span style={{ fontWeight: 600 }}>{multiPayments.map(p => `${p.title || 'Other'} (${Number(p.amount).toFixed(2)})`).join(', ')}</span>
            ) : (
              <span>{paymentMethod || 'Cash'} ({Number(payableTotal || 0).toFixed(2)})</span>
            )}
            {dueAmount > 0 && <span style={{ color: '#000', fontWeight: 700 }}> | Due: {Number(dueAmount).toFixed(2)}</span>}
          </div>
        </div>
      ) : (
        /* 80mm Side-by-side Layout with Ample Width */
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', margin: '4px 0' }}>
          {/* Left: Mode of Payment Box */}
          <div style={{ border: '1px solid #000', padding: '4px 5px', width: '38%', minHeight: '50px', fontSize: '10.5px' }}>
            <div style={{ fontWeight: 700, borderBottom: '1px solid #000', paddingBottom: '2px', marginBottom: '2px' }}>Mode Of Payment</div>
            {multiPayments && multiPayments.length > 0 ? (
              multiPayments.map((p, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
                  <span>{p.title || 'Other'}</span>
                  <span>{Number(p.amount).toFixed(2)}</span>
                </div>
              ))
            ) : (
              <>
                <div style={{ fontWeight: 600 }}>{paymentMethod || 'Cash'}</div>
                <div style={{ fontWeight: 600 }}>{Number(payableTotal || 0).toFixed(2)}</div>
              </>
            )}
            {dueAmount > 0 && <div style={{ fontWeight: 700, color: '#000', fontWeight: 700, fontSize: '9.5px', marginTop: '2px' }}>Due: {Number(dueAmount).toFixed(2)}</div>}
          </div>

          {/* Right: Totals Breakdown */}
          <div style={{ width: '60%', fontSize: '10.5px', lineHeight: '1.4' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingRight: '2px' }}>
              <span>Sub-Total :</span>
              <span>{subTotalNum.toFixed(2)}</span>
            </div>
            {discountNum > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingRight: '2px', color: '#000', fontWeight: 700 }}>
                <span>Discount {discountPercent > 0 ? `(${discountPercent}%)` : ''} :</span>
                <span>-{discountNum.toFixed(2)}</span>
              </div>
            )}
            {scNum > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingRight: '2px' }}>
                <span>Service Charge :</span>
                <span>+{scNum.toFixed(2)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingRight: '2px', fontWeight: 600 }}>
              <span>{finalIsVat ? 'Taxable AMT :' : 'NonTaxable AMT :'}</span>
              <span>{taxableAmt.toFixed(2)}</span>
            </div>
            {finalIsVat && taxNum > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingRight: '2px' }}>
                <span>13% VAT :</span>
                <span>+{taxNum.toFixed(2)}</span>
              </div>
            )}
            {roundOffNum !== 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingRight: '2px' }}>
                <span>Round Off :</span>
                <span>{roundOffNum > 0 ? `+${roundOffNum.toFixed(2)}` : roundOffNum.toFixed(2)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingRight: '2px', fontWeight: 700 }}>
              <span>Total :</span>
              <span>{grandTotalNum.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Grand Total Banner */}
      <div style={{
        borderTop: '1px solid #000',
        borderBottom: '1px solid #000',
        padding: is58 ? '3px 0' : '4px 0',
        margin: '4px 0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingRight: '2px'
      }}>
        <span style={{ fontSize: is58 ? '13px' : '15px', fontWeight: 900 }}>Grand Total:</span>
        <span style={{ fontSize: is58 ? '13px' : '15px', fontWeight: 900 }}>{grandTotalNum.toFixed(2)}</span>
      </div>

      {/* In Words & Received */}
      <div style={{ fontSize: is58 ? '9px' : '10.5px', marginTop: '3px', lineHeight: 1.3 }}>
        <div style={{ wordBreak: 'break-word' }}>In Words: {wordsOnly}</div>
        <div style={{ textAlign: 'right', marginTop: '3px', fontWeight: 600 }}>
          Received AMT: {Number(cashTendered || paidAmount || payableTotal || 0).toFixed(2)}
        </div>
        {dueAmount > 0 && (
          <div style={{ textAlign: 'right', color: '#000', fontWeight: 700 }}>
            Balance Due: {Number(dueAmount).toFixed(2)}
          </div>
        )}
      </div>

      {/* Thank You */}
      <div style={{ textAlign: 'center', fontSize: is58 ? '11px' : '13px', fontWeight: 700, margin: '8px 0 10px 0' }}>
        Thank You.
      </div>

      {/* Signatures */}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: is58 ? '9px' : '10px', marginTop: '10px', padding: '0 2px' }}>
        <div>
          <div style={{ width: is58 ? '60px' : '80px', borderTop: '1px solid #000', marginBottom: '2px' }}></div>
          <div>Cashier</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ width: is58 ? '60px' : '80px', borderTop: '1px solid #000', marginBottom: '2px', marginLeft: 'auto' }}></div>
          <div>Customer</div>
        </div>
      </div>

      {/* Footer Branding */}
      <div style={{ textAlign: 'center', fontSize: is58 ? '8.5px' : '9.5px', marginTop: '10px', color: '#333', lineHeight: 1.25 }}>
        {footer ? (
          <div>{footer}</div>
        ) : (
          <>
            <div>Powered By : STJ Technologies</div>
            <div>www.ehospitalitynepal.com</div>
          </>
        )}
      </div>
    </div>
  );
}

// =========================================================================
// 1. THERMAL IRD BILL (80mm / 58mm)
// =========================================================================
function ThermalIRDBill(props) {
  const {
    store_name, address, phone, finalPanNo, finalIsVat, billInvoiceNo, fiscalYear,
    nowLocale, deliveryType, table_title, buyerName, buyerPan, buyerPhone,
    cartItems, addonString, show_notes, fmt, itemsTotal, taxTotal,
    serviceChargeTotal, storedDiscount, roundOff, payableTotal, paidAmount, dueAmount, cashTendered, changeAmount,
    paymentMethod, multiPayments, amountWords, footer, header, tokenNo, print_token,
    paymentTypeDetails, currency, is58, storeImage, isEstimate
  } = props;

  return (
    <div style={{
      width: is58 ? '54mm' : '76mm',
      margin: '0 auto',
      padding: '4mm 2mm',
      fontFamily: "'Courier New', Courier, monospace",
      fontSize: is58 ? '11px' : '12px',
      lineHeight: 1.35,
      color: '#000'
    }}>
      {/* Store Header */}
      <div style={{ textAlign: 'center' }}>
        {storeImage && (
          <img
            src={getImageURL(storeImage)}
            alt="Logo"
            style={{ width: is58 ? 44 : 56, height: is58 ? 44 : 56, objectFit: 'cover', filter: 'grayscale(100%)', display: 'block', margin: '0 auto 4px' }}
          />
        )}
        <div style={{ fontWeight: 900, fontSize: is58 ? '14px' : '16px', textTransform: 'uppercase' }}>
          {store_name || 'RestroPro Restaurant'}
        </div>
        {address && <div style={{ fontSize: is58 ? '9.5px' : '10.5px' }}>{address}</div>}
        {phone && <div style={{ fontSize: is58 ? '9.5px' : '10.5px' }}>Tel: {phone}</div>}
        {finalPanNo && (
          <div style={{ fontWeight: 700, fontSize: is58 ? '10px' : '11px', marginTop: '2px' }}>
            {finalIsVat ? 'VAT' : 'PAN'} No: {finalPanNo}
          </div>
        )}
        <div style={{
          display: 'inline-block',
          border: '1px solid #000',
          padding: '1px 8px',
          fontWeight: 900,
          fontSize: is58 ? '10px' : '11px',
          margin: '4px 0 2px',
          textTransform: 'uppercase'
        }}>
          {isEstimate ? 'ESTIMATE BILL (कच्चा बिल)' : 'TAX INVOICE (कर बिजक)'}
        </div>
        {isEstimate && (
          <div style={{ fontSize: '8.5px', fontWeight: 'bold' }}>
            * This is not a tax invoice (यो कर बिजक होइन) *
          </div>
        )}
      </div>

      <div style={{ borderTop: '1px dashed #000', margin: '5px 0' }} />

      {/* Invoice Meta */}
      <table style={{ width: '100%', fontSize: is58 ? '9.5px' : '10.5px' }}>
        <tbody>
          <tr>
            <td><b>{isEstimate ? 'Estimate No:' : 'Invoice No:'}</b></td>
            <td style={{ textAlign: 'right' }}>{billInvoiceNo}</td>
          </tr>
          <tr>
            <td>Fiscal Year (आ.व.):</td>
            <td style={{ textAlign: 'right' }}>{fiscalYear}</td>
          </tr>
          <tr>
            <td>Date & Time (मिति):</td>
            <td style={{ textAlign: 'right' }}>{nowLocale}</td>
          </tr>
          {deliveryType && (
            <tr>
              <td>Billing Type:</td>
              <td style={{ textAlign: 'right', textTransform: 'uppercase' }}>{deliveryType}</td>
            </tr>
          )}
          {table_title && (
            <tr>
              <td>Table / Space:</td>
              <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{table_title}</td>
            </tr>
          )}
          <tr>
            <td>Buyer (क्रेता):</td>
            <td style={{ textAlign: 'right' }}>{buyerName}</td>
          </tr>
          {buyerPan && buyerPan !== 'N/A' && (
            <tr>
              <td>Buyer PAN (क्रेताको प्यान):</td>
              <td style={{ textAlign: 'right' }}>{buyerPan}</td>
            </tr>
          )}
          {buyerPhone && (
            <tr>
              <td>Contact:</td>
              <td style={{ textAlign: 'right' }}>{buyerPhone}</td>
            </tr>
          )}
        </tbody>
      </table>

      <div style={{ borderTop: '1px dashed #000', margin: '5px 0' }} />

      {/* Items Table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: is58 ? '10px' : '11px' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #000' }}>
            <th style={{ textAlign: 'left', padding: '2px 0' }}>SN</th>
            <th style={{ textAlign: 'left', padding: '2px 0' }}>Item (विवरण)</th>
            <th style={{ textAlign: 'center', padding: '2px 0' }}>Qty</th>
            <th style={{ textAlign: 'right', padding: '2px 0' }}>Rate</th>
            <th style={{ textAlign: 'right', padding: '2px 0' }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {cartItems?.map((it, idx) => (
            <tr key={idx} style={{ verticalAlign: 'top' }}>
              <td style={{ padding: '2px 0' }}>{idx + 1}</td>
              <td style={{ padding: '2px 0' }}>
                <div>{it.title || it.item_title}</div>
                {it.variant && <div style={{ fontSize: '9px', color: '#333' }}>- {it.variant.title}</div>}
                {it.addons_ids && it.addons_ids.length > 0 && (
                  <div style={{ fontSize: '8.5px', color: '#555' }}>+ {addonString(it.addons, it.addons_ids)}</div>
                )}
                {show_notes === 1 && it.notes && (
                  <div style={{ fontSize: '8.5px', fontStyle: 'italic' }}>Note: {it.notes}</div>
                )}
              </td>
              <td style={{ textAlign: 'center', padding: '2px 0' }}>{it.quantity}</td>
              <td style={{ textAlign: 'right', padding: '2px 0' }}>{fmt(it.price + (it.variant?.price || 0))}</td>
              <td style={{ textAlign: 'right', padding: '2px 0' }}>{fmt(it.quantity * (it.price + (it.variant?.price || 0)))}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ borderTop: '1px dashed #000', margin: '5px 0' }} />

      {/* Tax Calculation Schedule */}
      <table style={{ width: '100%', fontSize: is58 ? '10px' : '11px' }}>
        <tbody>
          <tr>
            <td>Gross Subtotal (जम्मा रकम):</td>
            <td style={{ textAlign: 'right' }}>{fmt(itemsTotal)}</td>
          </tr>
          {storedDiscount > 0 && (
            <tr>
              <td>Discount (छुट):</td>
              <td style={{ textAlign: 'right' }}>- {fmt(storedDiscount)}</td>
            </tr>
          )}
          {serviceChargeTotal > 0 && (
            <tr>
              <td>Service Charge:</td>
              <td style={{ textAlign: 'right' }}>{fmt(serviceChargeTotal)}</td>
            </tr>
          )}
          <tr>
            <td>Taxable Amount (करयोग्य रकम):</td>
            <td style={{ textAlign: 'right' }}>{fmt(itemsTotal - storedDiscount + serviceChargeTotal)}</td>
          </tr>
          {taxTotal > 0 && (
            <tr>
              <td>{finalIsVat ? '13% VAT (मू.अ.कर):' : 'Tax:'}</td>
              <td style={{ textAlign: 'right' }}>{fmt(taxTotal)}</td>
            </tr>
          )}
          {roundOff !== 0 && (
            <tr>
              <td>Round Off (राउन्ड अफ):</td>
              <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                {roundOff > 0 ? `+ ${fmt(roundOff)}` : `- ${fmt(Math.abs(roundOff))}`}
              </td>
            </tr>
          )}
          <tr style={{ fontWeight: 900, fontSize: is58 ? '12px' : '13px', borderTop: '1px solid #000', borderBottom: '1px solid #000' }}>
            <td>{isEstimate ? 'Estimated Total:' : 'Net Payable (जम्मा):'}</td>
            <td style={{ textAlign: 'right' }}>{fmt(payableTotal)}</td>
          </tr>
          {paidAmount !== undefined && (
            <tr>
              <td>Paid Amount (तिरेको रकम):</td>
              <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{fmt(paidAmount)}</td>
            </tr>
          )}
          {dueAmount > 0 && (
            <tr style={{ fontWeight: 'bold' }}>
              <td>Due Amount (बाँकी रकम):</td>
              <td style={{ textAlign: 'right' }}>{fmt(dueAmount)}</td>
            </tr>
          )}
          {cashTendered > 0 && (
            <>
              <tr><td>Cash Tendered:</td><td style={{ textAlign: 'right' }}>{fmt(cashTendered)}</td></tr>
              <tr><td>Change Return:</td><td style={{ textAlign: 'right' }}>{fmt(changeAmount)}</td></tr>
            </>
          )}
          {multiPayments && multiPayments.length > 0 ? (
            multiPayments.map((p, i) => (
              <tr key={i}><td>Payment Mode ({p.title || 'Other'}):</td><td style={{ textAlign: 'right', fontWeight: 'bold' }}>{Number(p.amount).toFixed(2)}</td></tr>
            ))
          ) : paymentMethod && (
            <tr><td>Payment Mode:</td><td style={{ textAlign: 'right', fontWeight: 'bold' }}>{paymentMethod}</td></tr>
          )}
        </tbody>
      </table>

      <div style={{ borderTop: '1px dashed #000', margin: '5px 0' }} />

      {/* In Words */}
      <div style={{ fontSize: '9.5px', marginTop: '3px' }}>
        <b>In Words (अक्षरेपी):</b><br/>
        <i>{amountWords}</i>
      </div>

      <div style={{ borderTop: '1px dashed #000', margin: '6px 0' }} />

      {/* Token Circle */}
      {print_token === 1 && tokenNo && (
        <div style={{ textAlign: 'center', margin: '8px 0' }}>
          <div style={{ fontSize: '10px', fontWeight: 'bold' }}>TOKEN NUMBER</div>
          <div style={{ width: '48px', height: '48px', lineHeight: '48px', border: '2px solid #000', borderRadius: '50%', margin: '4px auto', fontSize: '20px', fontWeight: 900 }}>
            {tokenNo}
          </div>
        </div>
      )}

      {/* QR Code */}
      {paymentTypeDetails?.qr_config && (
        <div style={{ textAlign: 'center', marginTop: '10px' }}>
          <div style={{ fontSize: '9px', fontWeight: 'bold', marginBottom: '3px' }}>Scan to Pay / Verify</div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <QRCodeSVG value={paymentTypeDetails.qr_config} size={88} level="M" />
          </div>
        </div>
      )}

      {/* Mandatory IRD Declarations */}
      <div style={{ textAlign: 'center', fontSize: '8.5px', marginTop: '10px', color: '#222' }}>
        {footer && <div style={{ fontWeight: 'bold', marginBottom: '3px' }}>{footer}</div>}
        {isEstimate ? (
          <>
            <div style={{ fontWeight: 'bold', color: '#000', fontSize: '9px' }}>
              *** ESTIMATE BILL ONLY — THIS IS NOT A TAX INVOICE ***
            </div>
            <div>यो केवल अनुमानित बिल हो, यसलाई कर बिजक मानिने छैन । भुक्तानी पश्चात् आधिकारिक कर बिजक जारी गरिनेछ ।</div>
          </>
        ) : (
          <>
            <div>माल सामान तथा सेवा फिर्ता हुँदैन।</div>
            <div>THIS IS A COMPUTER GENERATED TAX INVOICE</div>
            <div style={{ marginTop: '2px', fontWeight: 'bold' }}>Thank you for dining with us!</div>
          </>
        )}
      </div>
    </div>
  );
}

// =========================================================================
// 2. MODERN COMPACT POS BILL (80mm / 58mm)
// =========================================================================
function ModernPOSBill(props) {
  const {
    store_name, address, phone, finalPanNo, finalIsVat, billInvoiceNo, fiscalYear,
    nowLocale, deliveryType, table_title, buyerName, buyerPan,
    cartItems, addonString, show_notes, fmt, itemsTotal, taxTotal,
    serviceChargeTotal, storedDiscount, roundOff, payableTotal, paidAmount, dueAmount, cashTendered, changeAmount,
    paymentMethod, multiPayments, amountWords, footer, tokenNo, print_token, currency, is58, isEstimate
  } = props;

  return (
    <div style={{
      width: is58 ? '54mm' : '76mm',
      margin: '0 auto',
      padding: '4mm 2mm',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      fontSize: is58 ? '11px' : '12px',
      lineHeight: 1.35,
      color: '#111'
    }}>
      <div style={{ textAlign: 'center' }}>
        <span style={{
          display: 'inline-block',
          padding: '2px 8px',
          fontSize: '9px',
          fontWeight: 900,
          background: isEstimate ? '#d97706' : '#111',
          color: '#fff',
          borderRadius: '4px',
          letterSpacing: '0.5px'
        }}>
          {isEstimate ? 'ESTIMATE BILL (कर बिजक होइन)' : (finalIsVat ? 'TAX INVOICE' : 'BILL OF SUPPLY')}
        </span>
        <div style={{ fontSize: is58 ? '16px' : '18px', fontWeight: 900, letterSpacing: '-0.4px', margin: '3px 0 1px' }}>{store_name || 'RestroPro'}</div>
        <div style={{ fontSize: '10px', color: '#52525b' }}>{address}</div>
        {phone && <div style={{ fontSize: '10px', color: '#52525b' }}>Tel: {phone}</div>}
        {finalPanNo && <div style={{ fontSize: '11px', fontWeight: 800, marginTop: '2px' }}>{finalIsVat ? 'VAT' : 'PAN'}: {finalPanNo}</div>}
      </div>

      <div style={{ background: '#f4f4f5', borderRadius: '6px', padding: '6px', margin: '6px 0', fontSize: '10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>{isEstimate ? 'Estimate Ref:' : 'Bill No:'}</span><strong>{billInvoiceNo}</strong></div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Fiscal Year:</span><strong>{fiscalYear}</strong></div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Date:</span><span>{nowLocale}</span></div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Type:</span><strong>{deliveryType || 'Dine-In'}{table_title ? ` (${table_title})` : ''}</strong></div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Customer:</span><strong>{buyerName}</strong></div>
        {buyerPan !== 'N/A' && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Buyer PAN:</span><strong>{buyerPan}</strong></div>}
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', margin: '6px 0', fontSize: '10.5px' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #111' }}>
            <th style={{ textAlign: 'left', padding: '3px 0' }}>Item</th>
            <th style={{ textAlign: 'center', width: '15%', padding: '3px 0' }}>Qty</th>
            <th style={{ textAlign: 'right', width: '30%', padding: '3px 0' }}>Amt</th>
          </tr>
        </thead>
        <tbody>
          {cartItems.map((it, idx) => (
            <tr key={idx} style={{ borderBottom: '1px dashed #e4e4e7', verticalAlign: 'top' }}>
              <td style={{ padding: '4px 0' }}>
                <strong>{it.title}</strong>{it.variant?.title ? ` - ${it.variant.title}` : ''}
                {addonString(it.addons, it.addons_ids) && <div style={{ fontSize: '9px', color: '#71717a' }}>+ {addonString(it.addons, it.addons_ids)}</div>}
              </td>
              <td style={{ textAlign: 'center', padding: '4px 0' }}>{it.quantity}</td>
              <td style={{ textAlign: 'right', padding: '4px 0', fontWeight: 'bold' }}>{fmt(it.quantity * it.price)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ background: '#fafafa', border: '1px solid #e4e4e7', borderRadius: '6px', padding: '6px', margin: '6px 0', fontSize: '10.5px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1px 0' }}><span>Subtotal:</span><span>{fmt(itemsTotal)}</span></div>
        {storedDiscount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1px 0' }}><span>Discount:</span><span>- {fmt(storedDiscount)}</span></div>}
        {serviceChargeTotal > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1px 0' }}><span>Service Charge:</span><span>{fmt(serviceChargeTotal)}</span></div>}
        {taxTotal > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1px 0' }}><span>VAT (13%):</span><span>{fmt(taxTotal)}</span></div>}
        {Number(roundOff || 0) !== 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1px 0' }}>
            <span>Round Off:</span>
            <span>{Number(roundOff) > 0 ? `+ ${fmt(roundOff)}` : `- ${fmt(Math.abs(roundOff))}`}</span>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #111', paddingTop: '4px', marginTop: '2px', fontWeight: 900, fontSize: '12px' }}>
          <span>{isEstimate ? 'Estimated Total:' : 'Net Payable:'}</span><span>{fmt(payableTotal)}</span>
        </div>
        {!isEstimate && Number(paidAmount || 0) > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0 1px' }}>
            <span>Paid Amount:</span><span>{fmt(paidAmount)}</span>
          </div>
        )}
        {!isEstimate && Number(dueAmount || 0) > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0 1px', color: '#dc2626', fontWeight: 'bold' }}>
            <span>Due Amount:</span><span>{fmt(dueAmount)}</span>
          </div>
        )}
      </div>

      <div style={{ fontSize: '9px', margin: '4px 0', borderTop: '1px dashed #d1d5db', paddingTop: '4px', textAlign: 'left' }}>
        {multiPayments && multiPayments.length > 0 ? (
          multiPayments.map((p, i) => (
            <div key={i}><strong>Payment ({p.title || 'Other'}):</strong> {Number(p.amount).toFixed(2)}</div>
          ))
        ) : paymentMethod && (
          <div><strong>Payment Mode:</strong> {paymentMethod}</div>
        )}
      </div>

      <div style={{ fontSize: '9px', color: '#52525b', background: '#f4f4f5', padding: '4px 6px', borderRadius: '4px', margin: '4px 0' }}>
        <b>In Words:</b> <i>{amountWords}</i>
      </div>

      {print_token === 1 && tokenNo && (
        <div style={{ textAlign: 'center', margin: '8px 0', fontWeight: 900 }}>Token: #{tokenNo}</div>
      )}

      <div style={{ textAlign: 'center', fontSize: '8.5px', color: '#71717a', marginTop: '8px' }}>
        {footer && <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>{footer}</div>}
        <div>{isEstimate ? 'PROFORMA ESTIMATE • NOT A TAX INVOICE (अनुमानित बिल)' : 'Computer Generated Invoice • IRD Compliant'}</div>
        <div>Thank you! Please visit again.</div>
      </div>
    </div>
  );
}

// =========================================================================
// 3. STANDARD IRD TAX INVOICE (A4 / A5 LETTERHEAD)
// =========================================================================
function StandardA4A5Bill(props) {
  const {
    store_name, address, phone, email, finalPanNo, finalIsVat, billInvoiceNo,
    fiscalYear, nowLocale, deliveryType, table_title, buyerName, buyerPan,
    buyerPhone, cartItems, addonString, fmt, itemsTotal, taxTotal,
    serviceChargeTotal, storedDiscount, roundOff, payableTotal, paidAmount, dueAmount, paymentMethod, multiPayments, amountWords,
    footer, header, currency, storeImage, isEstimate
  } = props;

  return (
    <div style={{
      maxWidth: '680px',
      margin: '0 auto',
      padding: '16px',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif",
      fontSize: '11px',
      lineHeight: 1.45,
      color: '#1f2937'
    }}>
      {/* Letterhead Header */}
      <table style={{ width: '100%', borderBottom: `2px solid ${isEstimate ? '#d97706' : '#059669'}`, paddingBottom: '8px', marginBottom: '12px' }}>
        <tbody>
          <tr>
            <td style={{ verticalAlign: 'top' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {storeImage && (
                  <img src={getImageURL(storeImage)} alt="Logo" style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '8px' }} />
                )}
                <div>
                  <h1 style={{ fontSize: '20px', fontWeight: 900, color: isEstimate ? '#92400e' : '#065f46', margin: 0 }}>{store_name || 'RestroPro'}</h1>
                  <div style={{ fontSize: '10.5px', color: '#4b5563', marginTop: '2px' }}>
                    ${address}${phone ? ` • Phone: ${phone}` : ''}${email ? ` • Email: ${email}` : ''}<br/>
                    <strong>${finalIsVat ? 'VAT' : 'PAN'} Reg. No:</strong> {finalPanNo || 'N/A'}
                  </div>
                </div>
              </div>
            </td>
            <td style={{ textAlign: 'right', verticalAlign: 'top' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 900, color: isEstimate ? '#b45309' : '#111827', margin: 0 }}>
                {isEstimate ? 'PROFORMA INVOICE' : 'TAX INVOICE'}
              </h2>
              <div style={{ fontSize: '11px', fontWeight: 'bold', color: isEstimate ? '#b45309' : '#065f46' }}>
                {isEstimate ? 'अनुमानित बिल (यो कर बिजक होइन / NOT A TAX INVOICE)' : 'कर बिजक'}
              </div>
              <div style={{ fontSize: '10.5px', color: '#6b7280', marginTop: '2px' }}>
                {isEstimate ? 'Estimate Ref:' : 'Invoice No:'} <b>{billInvoiceNo}</b><br/>
                Fiscal Year (आ.व.): <b>{fiscalYear}</b>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      {header && <div style={{ textAlign: 'center', fontSize: '10.5px', fontStyle: 'italic', marginBottom: '8px' }}>{header}</div>}

      {isEstimate && (
        <div style={{
          backgroundColor: '#fffbeb',
          border: '1px dashed #f59e0b',
          color: '#92400e',
          padding: '6px 12px',
          borderRadius: '6px',
          textAlign: 'center',
          fontSize: '11px',
          fontWeight: 'bold',
          marginBottom: '12px'
        }}>
          ⚠️ THIS IS AN ESTIMATE BILL / PROFORMA INVOICE ONLY — NOT A FINAL TAX INVOICE (यो कर बिजक होइन)
        </div>
      )}

      {/* 2-Column Meta Grid */}
      <div style={{ display: 'flex', justifyContent: 'space-between', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 14px', marginBottom: '14px', fontSize: '10.5px' }}>
        <div style={{ width: '48%' }}>
          <p style={{ margin: '2px 0' }}><strong>Buyer / Customer:</strong> {buyerName}</p>
          <p style={{ margin: '2px 0' }}><strong>Buyer PAN (प्यान नं.):</strong> {buyerPan}</p>
          {buyerPhone && <p style={{ margin: '2px 0' }}><strong>Phone:</strong> {buyerPhone}</p>}
        </div>
        <div style={{ width: '48%', textAlign: 'right' }}>
          <p style={{ margin: '2px 0' }}><strong>Date & Time:</strong> {nowLocale}</p>
          <p style={{ margin: '2px 0' }}><strong>Order Type:</strong> {deliveryType || 'Dine-In'}{table_title ? (table_title.toLowerCase().startsWith('room') || deliveryType === 'room' ? ` (${table_title})` : ` (Table: ${table_title})`) : ''}</p>
          {multiPayments && multiPayments.length > 0 ? (
            multiPayments.map((p, i) => (
              <p key={i} style={{ margin: '2px 0' }}><strong>Payment Mode ({p.title || 'Other'}):</strong> {Number(p.amount).toFixed(2)}</p>
            ))
          ) : (
            <p style={{ margin: '2px 0' }}><strong>Payment Mode:</strong> {paymentMethod || (isEstimate ? 'Pending Settlement' : 'Cash')}</p>
          )}
        </div>
      </div>

      {/* Items Table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '14px' }}>
        <thead>
          <tr style={{ background: '#f3f4f6' }}>
            <th style={{ width: '6%', textAlign: 'center', padding: '6px 8px', border: '1px solid #d1d5db', fontSize: '10px' }}>SN</th>
            <th style={{ width: '52%', textAlign: 'left', padding: '6px 8px', border: '1px solid #d1d5db', fontSize: '10px' }}>Description of Goods / Services</th>
            <th style={{ width: '12%', textAlign: 'center', padding: '6px 8px', border: '1px solid #d1d5db', fontSize: '10px' }}>Qty</th>
            <th style={{ width: '15%', textAlign: 'right', padding: '6px 8px', border: '1px solid #d1d5db', fontSize: '10px' }}>Rate ({currency})</th>
            <th style={{ width: '15%', textAlign: 'right', padding: '6px 8px', border: '1px solid #d1d5db', fontSize: '10px' }}>Amount ({currency})</th>
          </tr>
        </thead>
        <tbody>
          {cartItems.map((it, idx) => {
            const price = Number(it.price || 0);
            const qty = Number(it.quantity || 1);
            return (
              <tr key={idx}>
                <td style={{ textAlign: 'center', padding: '6px 8px', border: '1px solid #e5e7eb' }}>{idx + 1}</td>
                <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb' }}>
                  <strong>{it.title}</strong>{it.variant?.title ? ` - ${it.variant.title}` : ''}
                  {addonString(it.addons, it.addons_ids) && <div style={{ fontSize: '9px', color: '#6b7280' }}>+ {addonString(it.addons, it.addons_ids)}</div>}
                </td>
                <td style={{ textAlign: 'center', padding: '6px 8px', border: '1px solid #e5e7eb' }}>{qty}</td>
                <td style={{ textAlign: 'right', padding: '6px 8px', border: '1px solid #e5e7eb' }}>{price.toFixed(2)}</td>
                <td style={{ textAlign: 'right', padding: '6px 8px', border: '1px solid #e5e7eb', fontWeight: 'bold' }}>{(qty * price).toFixed(2)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Summary Box */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <div style={{ width: '55%', padding: '10px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '10.5px' }}>
          <strong>Amount in Words (अक्षरेपी):</strong><br/>
          <i style={{ color: '#374151' }}>{amountWords}</i>
          <div style={{ marginTop: '10px', fontSize: '9px', color: '#6b7280' }}>
            {isEstimate ? (
              <>• यो केवल अनुमानित बिल हो । भुक्तानी पश्चात् आधिकारिक कर बिजक जारी गरिनेछ ।</>
            ) : (
              <>
                • माल सामान तथा सेवा फिर्ता हुँदैन।<br/>
                • This is a computer generated invoice in compliance with Nepal IRD guidelines.
              </>
            )}
          </div>
        </div>

        <table style={{ width: '40%', fontSize: '11px' }}>
          <tbody>
            <tr><td>Gross Subtotal:</td><td style={{ textAlign: 'right' }}>{fmt(itemsTotal)}</td></tr>
            {storedDiscount > 0 && <tr><td>Discount (छुट):</td><td style={{ textAlign: 'right' }}>- {fmt(storedDiscount)}</td></tr>}
            {serviceChargeTotal > 0 && <tr><td>Service Charge:</td><td style={{ textAlign: 'right' }}>{fmt(serviceChargeTotal)}</td></tr>}
            <tr><td>Taxable Amount:</td><td style={{ textAlign: 'right' }}>{fmt(itemsTotal - storedDiscount + serviceChargeTotal)}</td></tr>
            {taxTotal > 0 && <tr><td>{finalIsVat ? '13% VAT (मू.अ.कर):' : 'Tax:'}</td><td style={{ textAlign: 'right' }}>{fmt(taxTotal)}</td></tr>}
            {Number(roundOff || 0) !== 0 && (
              <tr>
                <td>Round Off (राउन्ड अफ):</td>
                <td style={{ textAlign: 'right' }}>{Number(roundOff) > 0 ? `+ ${fmt(roundOff)}` : `- ${fmt(Math.abs(roundOff))}`}</td>
              </tr>
            )}
            <tr style={{ borderTop: '2px solid #111827', borderBottom: '2px solid #111827', fontWeight: 900, fontSize: '13px' }}>
              <td>{isEstimate ? 'Estimated Total:' : 'Grand Total:'}</td><td style={{ textAlign: 'right' }}>{fmt(payableTotal)}</td>
            </tr>
            {!isEstimate && Number(paidAmount || 0) > 0 && (
              <tr>
                <td>Paid Amount (तिरेको रकम):</td>
                <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{fmt(paidAmount)}</td>
              </tr>
            )}
            {!isEstimate && Number(dueAmount || 0) > 0 && (
              <tr style={{ color: '#dc2626', fontWeight: 'bold' }}>
                <td>Due Amount (बाँकी रकम):</td>
                <td style={{ textAlign: 'right' }}>{fmt(dueAmount)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Dual Signature Blocks */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '40px', paddingTop: '10px' }}>
        <div style={{ width: '35%', textAlign: 'center', borderTop: '1px dashed #4b5563', paddingTop: '4px', fontWeight: 'bold', fontSize: '10px' }}>
          Customer Signature
        </div>
        <div style={{ width: '35%', textAlign: 'center', borderTop: '1px dashed #4b5563', paddingTop: '4px', fontWeight: 'bold', fontSize: '10px' }}>
          Authorized Signatory<br/><span style={{ fontSize: '8.5px', fontWeight: 'normal' }}>For {store_name || 'RestroPro'}</span>
        </div>
      </div>

      {footer && <div style={{ textAlign: 'center', fontSize: '9px', color: '#6b7280', marginTop: '20px' }}>{footer}</div>}
      {isEstimate && (
        <div style={{ textAlign: 'center', fontSize: '9px', color: '#b45309', fontWeight: 'bold', marginTop: '6px' }}>
          *** ESTIMATE ONLY — NOT A TAX INVOICE. Official Tax Invoice will be issued upon payment. ***
        </div>
      )}
    </div>
  );
}

// =========================================================================
// 4. LUXURY FOLIO BILL (A4 / A5 LUXURY)
// =========================================================================
function LuxuryFolioBill(props) {
  const {
    store_name, address, phone, finalPanNo, finalIsVat, billInvoiceNo,
    fiscalYear, nowLocale, deliveryType, table_title, buyerName, buyerPan,
    cartItems, addonString, fmt, itemsTotal, taxTotal, serviceChargeTotal,
    storedDiscount, roundOff, payableTotal, paidAmount, dueAmount, paymentMethod, multiPayments, amountWords, footer, currency, isEstimate
  } = props;

  return (
    <div style={{
      maxWidth: '680px',
      margin: '0 auto',
      padding: '16px',
      fontFamily: "'Garamond', 'Georgia', serif",
      fontSize: '11.5px',
      lineHeight: 1.45,
      color: '#1c1917'
    }}>
      {/* Luxury Header */}
      <div style={{ borderBottom: '3px double #d97706', paddingBottom: '10px', marginBottom: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: 900, color: isEstimate ? '#92400e' : '#78350f', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>
            {store_name || 'RestroPro'}
          </h1>
          <div style={{ fontSize: '10px', color: '#57534e', marginTop: '2px' }}>
            {address}{phone ? ` • Tel: ${phone}` : ''}<br/>
            <strong>{finalIsVat ? 'VAT' : 'PAN'} Registration:</strong> {finalPanNo || 'N/A'}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <h2 style={{ fontSize: '16px', fontWeight: 900, color: isEstimate ? '#b45309' : '#78350f', margin: 0, letterSpacing: '1px' }}>
            {isEstimate ? 'PROFORMA ESTIMATE' : 'EXECUTIVE INVOICE'}
          </h2>
          <div style={{ fontSize: '10px', color: isEstimate ? '#b45309' : '#78350f', fontWeight: 'bold' }}>
            {isEstimate ? 'अनुमानित बिल (कर बिजक होइन)' : 'कर बिजक / TAX INVOICE'}
          </div>
          <div style={{ fontSize: '10px', color: '#78716c' }}>
            {isEstimate ? 'Est Ref' : 'Inv'}: {billInvoiceNo} | FY: {fiscalYear}
          </div>
        </div>
      </div>

      {isEstimate && (
        <div style={{
          background: '#fef3c7',
          borderLeft: '4px solid #d97706',
          padding: '6px 12px',
          marginBottom: '12px',
          fontSize: '10.5px',
          color: '#92400e',
          fontWeight: 'bold'
        }}>
          PROFORMA ESTIMATE: Presented for customer review prior to final settlement. Official Tax Invoice issued upon payment.
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', fontSize: '11px' }}>
        <div>
          <p style={{ margin: '2px 0' }}><strong>Distinguished Patron:</strong> {buyerName}</p>
          <p style={{ margin: '2px 0' }}><strong>Buyer PAN:</strong> {buyerPan}</p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ margin: '2px 0' }}><strong>Date & Time:</strong> {nowLocale}</p>
          <p style={{ margin: '2px 0' }}><strong>Service:</strong> {deliveryType || 'Fine Dining'}{table_title ? ` (${table_title})` : ''}</p>
          {multiPayments && multiPayments.length > 0 ? (
            multiPayments.map((p, i) => (
              <p key={i} style={{ margin: '2px 0' }}><strong>Payment Mode ({p.title || 'Other'}):</strong> {Number(p.amount).toFixed(2)}</p>
            ))
          ) : (
            <p style={{ margin: '2px 0' }}><strong>Payment Mode:</strong> {paymentMethod || (isEstimate ? 'Pending Settlement' : 'Cash')}</p>
          )}
        </div>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '14px' }}>
        <thead>
          <tr>
            <th style={{ background: isEstimate ? '#92400e' : '#78350f', color: '#fff', fontSize: '9.5px', textTransform: 'uppercase', padding: '6px 8px', textAlign: 'center', width: '6%' }}>#</th>
            <th style={{ background: isEstimate ? '#92400e' : '#78350f', color: '#fff', fontSize: '9.5px', textTransform: 'uppercase', padding: '6px 8px', textAlign: 'left', width: '54%' }}>Particulars</th>
            <th style={{ background: isEstimate ? '#92400e' : '#78350f', color: '#fff', fontSize: '9.5px', textTransform: 'uppercase', padding: '6px 8px', textAlign: 'center', width: '12%' }}>Qty</th>
            <th style={{ background: isEstimate ? '#92400e' : '#78350f', color: '#fff', fontSize: '9.5px', textTransform: 'uppercase', padding: '6px 8px', textAlign: 'right', width: '14%' }}>Rate</th>
            <th style={{ background: isEstimate ? '#92400e' : '#78350f', color: '#fff', fontSize: '9.5px', textTransform: 'uppercase', padding: '6px 8px', textAlign: 'right', width: '14%' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {cartItems.map((it, idx) => (
            <tr key={idx} style={{ borderBottom: '1px solid #f5f5f4' }}>
              <td style={{ textAlign: 'center', padding: '6px 8px', color: '#78716c' }}>{idx + 1}</td>
              <td style={{ padding: '6px 8px' }}>
                <strong>{it.title}</strong>{it.variant?.title ? ` - ${it.variant.title}` : ''}
                {addonString(it.addons, it.addons_ids) && <div style={{ fontSize: '9px', color: '#78716c' }}>+ {addonString(it.addons, it.addons_ids)}</div>}
              </td>
              <td style={{ textAlign: 'center', padding: '6px 8px' }}>{it.quantity}</td>
              <td style={{ textAlign: 'right', padding: '6px 8px' }}>{Number(it.price || 0).toFixed(2)}</td>
              <td style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 'bold' }}>{fmt(it.quantity * it.price)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: '10px' }}>
        <div style={{ width: '50%', borderLeft: '3px solid #d97706', paddingLeft: '10px', fontSize: '10px' }}>
          <strong>Amount in Words:</strong><br/>
          <i style={{ color: '#44403c' }}>{amountWords}</i>
        </div>

        <table style={{ width: '45%', fontSize: '11px' }}>
          <tbody>
            <tr><td>Subtotal:</td><td style={{ textAlign: 'right' }}>{fmt(itemsTotal)}</td></tr>
            {storedDiscount > 0 && <tr><td>Discount:</td><td style={{ textAlign: 'right' }}>- {fmt(storedDiscount)}</td></tr>}
            {serviceChargeTotal > 0 && <tr><td>Service Charge:</td><td style={{ textAlign: 'right' }}>{fmt(serviceChargeTotal)}</td></tr>}
            {taxTotal > 0 && <tr><td>VAT (13%):</td><td style={{ textAlign: 'right' }}>{fmt(taxTotal)}</td></tr>}
            {Number(roundOff || 0) !== 0 && (
              <tr>
                <td>Round Off:</td>
                <td style={{ textAlign: 'right' }}>{Number(roundOff) > 0 ? `+ ${fmt(roundOff)}` : `- ${fmt(Math.abs(roundOff))}`}</td>
              </tr>
            )}
            <tr style={{ borderTop: '2px solid #78350f', borderBottom: '2px solid #78350f', fontWeight: 900, color: '#78350f', fontSize: '13px' }}>
              <td>{isEstimate ? 'Estimated Total:' : 'Total Payable:'}</td><td style={{ textAlign: 'right' }}>{fmt(payableTotal)}</td>
            </tr>
            {!isEstimate && Number(paidAmount || 0) > 0 && (
              <tr>
                <td>Paid Amount:</td>
                <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{fmt(paidAmount)}</td>
              </tr>
            )}
            {!isEstimate && Number(dueAmount || 0) > 0 && (
              <tr style={{ color: '#000', fontWeight: 700 }}>
                <td>Due Amount:</td>
                <td style={{ textAlign: 'right' }}>{fmt(dueAmount)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '45px', fontSize: '10px' }}>
        <div style={{ width: '35%', textAlign: 'center', borderTop: '1px solid #78350f', paddingTop: '4px', fontWeight: 'bold' }}>
          Guest Signature
        </div>
        <div style={{ width: '35%', textAlign: 'center', borderTop: '1px solid #78350f', paddingTop: '4px', fontWeight: 'bold' }}>
          Authorized Signature<br/><span style={{ fontSize: '8px', fontWeight: 'normal' }}>For {store_name || 'RestroPro'}</span>
        </div>
      </div>

      {footer && <div style={{ textAlign: 'center', fontSize: '9px', color: '#78716c', marginTop: '15px' }}>{footer}</div>}
      {isEstimate && (
        <div style={{ textAlign: 'center', fontSize: '9px', color: '#b45309', marginTop: '8px', fontWeight: 'bold' }}>
          *** THIS IS A PROFORMA ESTIMATE FOLIO — NOT A TAX INVOICE ***
        </div>
      )}
    </div>
  );
}
